<template>
  <HeaderBar />

  <section class="carvia-container flex flex-col justify-center">
    <div class="mb-4 flex justify-between gap-4">
      <Input v-model="search" :placeholder="t('search')" class="w-full max-w-sm" />
      <CVButton :to="{ name: 'station', params: { id: 'new' } }">{{ t('newStation') }}</CVButton>
    </div>

    <div class="flex flex-col gap-2">
      <PaginatedList :load="load" :query-key="queryKey" :empty-text="t('noStationsFound')">
        <template #entry="{ entry: station }">
          <StationListItem :station="station" />
        </template>
      </PaginatedList>
    </div>
  </section>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import HeaderBar from '@/components/headerbar/HeaderBar.vue';
import { useUiStore } from '@/stores/ui.store';
import { computed, watchEffect } from 'vue';
import { useRouteQueryRef } from '@/hooks/use-route-query-ref';
import { STATIONS_QUERY_KEY } from '@/queries/use-stations';
import { stationsService } from '@/api/stations.service';
import StationListItem from './StationListItem.vue';
import PaginatedList from '@/components/PaginatedList.vue';
import Input from '@/components/Input.vue';
import { FindAllStationsSort } from '@/entities/station/find-all-stations-sort.enum';
import { Order } from '@/entities/pagination/order.enum';
import { refDebounced } from '@vueuse/core';

const { t } = useI18n();
const uiStore = useUiStore();
watchEffect(() => {
  uiStore.setHeaderTitle(t('stations'));
});

const search = useRouteQueryRef('search', '', 'string');
const searchQuery = refDebounced(search, 500);

const listQuery = computed(() => ({
  query: searchQuery.value,
  sort: FindAllStationsSort.CREATED_DATE,
  order: Order.ASC,
}));

const queryKey = computed(() => [STATIONS_QUERY_KEY, listQuery.value]);

const load = (page: number) =>
  stationsService.getAllPaginated({
    ...listQuery.value,
    page,
  });
</script>

<i18n lang="json">
{
  "en": {
    "stations": "Stations",
    "newStation": "+ New Station",
    "search": "Search",
    "noStationsFound": "No Stations found"
  },
  "de": {
    "stations": "Stationen",
    "newStation": "+ Neue Station",
    "search": "Suche",
    "noStationsFound": "Keine Stationen gefunden"
  },
  "es": {
    "stations": "Estaciones",
    "newStation": "+ Nueva estación",
    "search": "Buscar",
    "noStationsFound": "No se encontraron estaciones"
  }
}
</i18n>
